// Pure helpers behind the AI Diagnose UI — kept apart from the components so
// the rules (what a status looks like, when to poll, how a refusal reads) are
// tested without rendering anything.

import { isAxiosError } from 'axios';
import type { ReportConfidence, ReportStatus } from '../../../types/diagnostics';
import { SYMPTOM_MAX_LENGTH } from '../../../types/diagnostics';

/** How often an unfinished report is re-read. Claude usually takes under a minute. */
export const REPORT_POLL_MS = 3000;

/** WhatsApp's own share link; the recipient is chosen in WhatsApp. */
export function whatsappShareUrl(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export function isReportInFlight(status: ReportStatus | undefined): boolean {
  return status === 'pending' || status === 'running';
}

/**
 * react-query refetchInterval for a report: keep polling until it settles.
 * Before the first response there is no status yet — keep polling too.
 */
export function reportPollInterval(status: ReportStatus | undefined, ms = REPORT_POLL_MS): number | false {
  if (status === undefined) return ms;
  return isReportInFlight(status) ? ms : false;
}

export type ChipTone = 'info' | 'success' | 'warning' | 'neutral' | 'error';

export interface StatusChip {
  label: string;
  tone: ChipTone;
  spinner: boolean;
}

const CONFIDENCE_TONE: Record<ReportConfidence, ChipTone> = {
  high: 'success',
  medium: 'warning',
  low: 'neutral',
};

export function statusChip(
  report: { status: ReportStatus; confidence: ReportConfidence | null } | null | undefined,
): StatusChip | null {
  if (!report) return null;
  if (isReportInFlight(report.status)) return { label: 'Diagnosing…', tone: 'info', spinner: true };
  if (report.status === 'failed') return { label: 'Failed', tone: 'error', spinner: false };
  if (!report.confidence) return { label: 'Ready', tone: 'success', spinner: false };
  return { label: `Ready · ${report.confidence} confidence`, tone: CONFIDENCE_TONE[report.confidence], spinner: false };
}

/** Dark-slate chip classes, matching the bundle reason badges. */
export const CHIP_TONE_CLASSES: Record<ChipTone, string> = {
  info: 'bg-cyan-900/30 text-cyan-300 border border-cyan-700/50',
  success: 'bg-green-900/30 text-green-300 border border-green-700/50',
  warning: 'bg-amber-900/30 text-amber-300 border border-amber-700/50',
  neutral: 'bg-slate-700/50 text-slate-300 border border-slate-600/50',
  error: 'bg-red-900/30 text-red-300 border border-red-700/50',
};

export function confidenceLabel(confidence: ReportConfidence): string {
  return `${confidence.charAt(0).toUpperCase()}${confidence.slice(1)} confidence`;
}

export function confidenceTone(confidence: ReportConfidence): ChipTone {
  return CONFIDENCE_TONE[confidence];
}

export type DiagnoseErrorKind = 'already-running' | 'daily-cap' | 'not-configured' | 'other';

export interface DiagnoseError {
  kind: DiagnoseErrorKind;
  message: string;
  /** For already-running: the report in progress, when the API names it. */
  reportId: string | null;
}

/**
 * Turns a refused diagnose into something to show or act on. The contract
 * does not promise a report id with a 409, so the caller falls back to
 * listing the bundle's reports when this is null.
 */
export function diagnoseErrorOf(err: unknown): DiagnoseError {
  if (isAxiosError(err) || (err as { isAxiosError?: boolean })?.isAxiosError) {
    const response = (err as { response?: { status?: number; data?: Record<string, unknown> } }).response;
    const status = response?.status;
    const body = response?.data ?? {};
    const apiMessage = typeof body.error === 'string' ? body.error : typeof body.message === 'string' ? body.message : null;

    if (status === 409) {
      const data = body.data as { id?: unknown } | undefined;
      const id = typeof data?.id === 'string' ? data.id : typeof body.report_id === 'string' ? body.report_id : null;
      return { kind: 'already-running', message: 'A diagnosis of this bundle is already running.', reportId: id };
    }
    if (status === 429) {
      return { kind: 'daily-cap', message: 'The daily Diagnose limit has been reached. Try again tomorrow.', reportId: null };
    }
    if (status === 503) {
      return { kind: 'not-configured', message: 'Diagnose is not configured on this server yet.', reportId: null };
    }
    if (apiMessage) return { kind: 'other', message: apiMessage, reportId: null };
  }
  return { kind: 'other', message: 'Could not start the diagnosis. Please try again.', reportId: null };
}

export function normaliseSymptom(text: string): string | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, SYMPTOM_MAX_LENGTH);
}

// The last symptom typed for a device, so a re-run or a later bundle starts
// from what the skipper said. A per-viewer convenience: browser storage can be
// unavailable (private mode, blocked site data), so every access is guarded.
const LAST_SYMPTOM_KEY = 'vortex.diagnose.lastSymptom.';

export function readLastSymptom(deviceId: string): string {
  try {
    return localStorage.getItem(LAST_SYMPTOM_KEY + deviceId) ?? '';
  } catch {
    return '';
  }
}

export function rememberSymptom(deviceId: string, symptom: string | undefined): void {
  if (!symptom) return;
  try {
    localStorage.setItem(LAST_SYMPTOM_KEY + deviceId, symptom);
  } catch {
    // storage unavailable — the prefill is only a convenience
  }
}

export function formatCost(cents: number | null): string {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}
